import { useMemo } from 'react';
import ChapterSection from '../ChapterSection/ChapterSection';
import ChapterMap from '../ChapterMap/ChapterMap';
import TrendChart, { type TrendPoint } from './TrendChart';
import type { Point, ReinfoPortfolio } from '@/lib/api.types';
import {
  DID_OUTLINE_COLOR,
  INDUCEMENT_ZONE_COLOR,
  POP_TREND_BANDS,
  POP_TREND_NEUTRAL_COLOR,
  popTrendBand,
} from '@/lib/chapters';
import type { GeometryLayer, LegendItem, MapSpec } from '@/lib/map-spec';
import type { LeadSpec, RowSpec } from '@/lib/report-content';
import { TILES } from '@/lib/tiles';
import {
  classifyProspectTier,
  formatChangePct,
  parseDidDetails,
  parseInducement,
  parseProspectsMetrics,
  prospectVerdict,
} from '@/lib/prospects';
import { ChapterLead, CheckList, EmptyState, RawDetails, RowList } from './shared';
import styles from './ChapterProspects.module.css';

type Props = {
  portfolio: ReinfoPortfolio;
  /** 都市計画ポートフォリオ（任意）。立地適正化計画 (XKT003) の居住誘導区域を将来文脈で再利用する。 */
  planning?: ReinfoPortfolio;
  site: Point;
};

// 見通し章で常に確かめてほしい定番項目（読者が自分で動ける行動につなぐ）。
// 動的な項目（人口減少・高齢化）は backend の metrics["prospects"]["check_items"] から来る。
const CH5_CHECK_ITEMS = [
  '市区町村の人口ビジョン・公共施設の再編計画（小中学校の統廃合の予定など）',
  '立地適正化計画の区域図 — この土地が居住誘導区域の内か外か（自治体サイト）',
  '通学予定の小学校の児童数のうつりかわり（学校要覧・自治体統計）',
  '最寄り駅・バス路線の便数のうつりかわり（時刻表の改定）',
  '周辺の空き家・空き地のようす（朝夕の現地歩きで）',
];

const LEAD: LeadSpec = {
  label: '三十年さきのものがたり',
  message:
    '家を建ててからローンを払い終えるまでは、およそ三十年。国の人口推計とまちの計画から、この土地に流れる長い時間を読み解きます。未来を言い当てることはできませんが、備えるための手がかりになります。',
};

const EMPTY_TEXT =
  'この土地のまわりでは、将来のものがたりを描くための国の公開データ（人口集中地区・将来推計人口・立地適正化計画）が見つかりませんでした。データが無いことは、見通しが悪いことを意味しません。市区町村が公表する人口ビジョンや総合計画をあわせてご覧ください。';

const POPULATION_INTRO = 'この土地を含むおよそ250m四方では、人口がつぎのように推計されています。';
const POPULATION_NOTE =
  '※将来推計人口は、国の地域別推計をもとに国土交通省が約250m四方のメッシュへ割り付けた推計値です（国土数値情報）。開発や制度の変更で実際の歩みは変わり、少人数の地区では値が秘匿・合算されている場合があります。資産価値を約束する数字ではなく、長く住まうための備えの手がかりとしてお読みください。';
const SHAPE_INTRO =
  '人口集中地区は「市街地のまとまり」、居住誘導区域は「市町村が住まいを残していくと定めた範囲」です。土地がその内側にあるかを見ます。';

const formatPopulation = (v: number) => `約${Math.round(v).toLocaleString('ja-JP')}人`;

export type ProspectsPopulation = {
  intro: string;
  chart: TrendPoint[];
  formatValue: (v: number) => string;
  ariaLabel: string;
  agingText: string | null;
  note: string;
};

export type ProspectsContent = {
  /** データが何も無いときは verdict 以下を出さず emptyText だけ見せる。 */
  hasAnything: boolean;
  lead: LeadSpec;
  verdict: { heading: string; body: string };
  facts: string[];
  population: ProspectsPopulation | null;
  shape: { intro: string; maps: MapSpec[] };
  rows: RowSpec[];
  checkItems: string[];
  emptyText: string;
};

// 見通し章の中身。画面 (ChapterProspects) と PDF 冊子が共有する純関数。
export function buildProspectsContent(
  portfolio: ReinfoPortfolio,
  planning: ReinfoPortfolio | undefined,
  site: Point,
): ProspectsContent {
  const popLayer = portfolio.layers.find((l) => l.endpoint === 'XKT013');
  const didLayer = portfolio.layers.find((l) => l.endpoint === 'XKT031');
  const inducementLayer = planning?.layers.find((l) => l.endpoint === 'XKT003');

  const prospects = parseProspectsMetrics(portfolio.metrics);
  const population = prospects?.population;
  const stationTrend = prospects?.stationTrend;
  // 取得失敗(error/skipped)を「外」と断定しない。not_found(タイル内にDIDなし)は「外」として扱う。
  const didInside =
    didLayer && (didLayer.status === 'success' || didLayer.status === 'not_found')
      ? didLayer.matched_count > 0
      : null;
  const didDetails = parseDidDetails(didLayer);
  const inducement = parseInducement(planning);

  const tier = classifyProspectTier(population?.changePct);
  const verdict = prospectVerdict({
    tier,
    targetYear: population?.targetYear,
    changePct: population?.changePct,
    didInside,
    inducement,
  });

  // 総評カードの根拠チップ（事実のみを短く）。
  const facts: string[] = [];
  if (population) {
    facts.push(
      `人口 ${population.baseYear}→${population.targetYear}年 ${formatChangePct(population.changePct)}`,
    );
  }
  if (didInside != null) facts.push(`人口集中地区: ${didInside ? '内' : '外'}`);
  if (inducement === 'inside') facts.push('居住誘導区域: 内');
  else if (inducement === 'outside') facts.push('居住誘導区域: 該当なし');
  else if (inducement === 'zone_other') facts.push('立地適正化計画: 区域内');
  else if (inducement === 'unknown') facts.push('立地適正化計画: データなし');

  // メインマップ: 250mメッシュの人口増減コロプレス + DID破線アウトライン。
  // 着色は feature_metric_values（増減率%・index-aligned）で行い、ラベル文字列はパースしない。
  const meshGeos: GeometryLayer[] = [];
  const meshLegend: LegendItem[] = [];
  {
    const meshGeoms = popLayer?.feature_geometries ?? [];
    const meshLabels = popLayer?.feature_labels ?? [];
    const meshValues = popLayer?.feature_metric_values ?? [];
    const seenBands = new Set<string>();
    let hasNeutralMesh = false;
    meshGeoms.forEach((g, i) => {
      if (g.type !== 'Polygon' && g.type !== 'MultiPolygon') return;
      const value = typeof meshValues[i] === 'number' ? meshValues[i] : null;
      const band = popTrendBand(value);
      if (band) seenBands.add(band.label);
      else hasNeutralMesh = true;
      meshGeos.push({
        data: g,
        style: {
          stroke: false,
          fillColor: band?.color ?? POP_TREND_NEUTRAL_COLOR,
          fillOpacity: 0.32,
        },
        label: meshLabels[i] ?? popLayer?.value_label ?? popLayer?.name,
      });
    });
    for (const band of POP_TREND_BANDS) {
      if (seenBands.has(band.label)) meshLegend.push({ color: band.color, label: band.label });
    }
    if (hasNeutralMesh) meshLegend.push({ color: POP_TREND_NEUTRAL_COLOR, label: '推計なし' });

    // DID は塗りをやめ破線アウトラインに（メッシュの色分けと干渉させない）。
    const didGeoms = didLayer?.feature_geometries ?? [];
    let hasDid = false;
    didGeoms.forEach((g) => {
      if (g.type !== 'Polygon' && g.type !== 'MultiPolygon') return;
      hasDid = true;
      meshGeos.push({
        data: g,
        style: { color: DID_OUTLINE_COLOR, weight: 2, dashArray: '6 4', fill: false },
        label: didLayer?.value_label ?? '人口集中地区',
      });
    });
    if (hasDid) meshLegend.push({ color: DID_OUTLINE_COLOR, label: '人口集中地区 (DID)', outline: true });
  }

  const maps: MapSpec[] = [
    {
      key: 'mesh',
      point: site,
      tileUrl: TILES.pale,
      zoom: 14,
      height: 280,
      geometries: meshGeos,
      markers: [],
      legend: meshLegend,
      caption: '将来推計人口の増減（250mメッシュ）と人口集中地区',
      emptyMessage: 'この付近では将来見通しに関するデータが見つかりませんでした。',
    },
  ];

  // サブマップ: 立地適正化計画（居住誘導区域など）。geometry が無ければマップごと出さない。
  // MLIT はポリゴンをタイル境界でクリップして返すため、ストロークを描くとタイル格子線が
  // 区域内に走って偽の境界に見える — 塗りのみ(stroke なし)で隣接クリップ片をつなげる。
  {
    const geoms = inducementLayer?.feature_geometries ?? [];
    const labels = inducementLayer?.feature_labels ?? [];
    const geos: GeometryLayer[] = [];
    geoms.forEach((g, i) => {
      if (g.type !== 'Polygon' && g.type !== 'MultiPolygon') return;
      geos.push({
        data: g,
        style: {
          stroke: false,
          fillColor: INDUCEMENT_ZONE_COLOR,
          fillOpacity: 0.2,
        },
        label: labels[i] ?? inducementLayer?.value_label ?? '立地適正化計画',
      });
    });
    if (geos.length > 0) {
      maps.push({
        key: 'inducement',
        point: site,
        tileUrl: TILES.pale,
        // zoom はデータ取得契約(z14 3×3 タイル + RENDER_RADIUS_M=3.5km ≒ zoom14 ビューポート)に
        // 合わせて 14 固定。13 に広げるとビューポートがデータ範囲を超え、区域が偽の直線で「終わって」見える。
        zoom: 14,
        height: 220,
        geometries: geos,
        markers: [],
        legend: [{ color: INDUCEMENT_ZONE_COLOR, label: '立地適正化計画の区域' }],
        caption: '立地適正化計画（居住誘導区域など）',
        emptyMessage: 'この付近では立地適正化計画の区域データが見つかりませんでした。',
      });
    }
  }
  const hasInducementMap = maps.length > 1;

  const populationBlock: ProspectsPopulation | null = population
    ? {
        intro: POPULATION_INTRO,
        chart: population.series.map((s) => ({ label: `${s.year}年`, value: s.value })),
        formatValue: formatPopulation,
        ariaLabel: `${population.baseYear}年 約${population.base.toLocaleString('ja-JP')}人 から ${population.targetYear}年 約${population.target.toLocaleString('ja-JP')}人 へ`,
        agingText:
          population.agingBasePct != null && population.agingTargetPct != null
            ? `65歳以上の割合 ${population.baseYear}年 ${population.agingBasePct}% → ${population.targetYear}年 ${population.agingTargetPct}%`
            : null,
        note: POPULATION_NOTE,
      }
    : null;

  // 見通しの読みどころ（データがある行だけ）。
  const rows: RowSpec[] = [];
  if (population) {
    rows.push({
      icon: 'chart-line',
      label: '将来推計人口（約250m四方）',
      detail: `${population.baseYear}年 約${population.base.toLocaleString('ja-JP')}人 → ${population.targetYear}年 約${population.target.toLocaleString('ja-JP')}人`,
      // 人口動態は市場数値であって安全リスクではない(価格章と同じ思想)ため、
      // ◯/△ の安全判定記号は使わず neutral 固定。語彙は地図凡例と同じ正準バンド。
      status: 'neutral',
      statusLabel: popTrendBand(population.changePct)?.shortLabel ?? '推計あり',
    });
  }
  if (population?.agingBasePct != null && population?.agingTargetPct != null) {
    rows.push({
      icon: 'users-group',
      label: '65歳以上の割合の見通し',
      detail: `${population.baseYear}年 ${population.agingBasePct}% → ${population.targetYear}年 ${population.agingTargetPct}%`,
      status: 'neutral',
      statusLabel: population.agingTargetPct >= 35 ? '上昇見込み' : '推計あり',
    });
  }
  if (didLayer) {
    rows.push({
      icon: 'building-community',
      label: '人口集中地区（市街地のまとまり）',
      detail: didInside
        ? `市街地のまとまりの中にあります${
            didDetails?.population != null && didDetails.areaKm2 != null
              ? `。人口 約${didDetails.population.toLocaleString('ja-JP')}人・${didDetails.areaKm2}km²${
                  didDetails.changePct != null
                    ? `（前回調査比 ${formatChangePct(didDetails.changePct)}）`
                    : ''
                }`
              : ''
          }`
        : '市街地のまとまりの外側にあります。静かな住環境の一方、生活サービスの密度はまちの中心部とは異なります',
      status: didInside ? 'safe' : 'neutral',
      statusLabel: didInside ? '内' : '外',
    });
  }
  if (inducement != null) {
    rows.push({
      icon: 'map-2',
      label: '居住誘導区域（立地適正化計画）',
      detail:
        inducement === 'inside'
          ? '市町村が「住まいを残していく」と定めた区域の内側です'
          : inducement === 'zone_other'
            ? '立地適正化計画の区域内ですが、区分までは確認できませんでした。自治体の区域図でご確認ください'
            : inducement === 'outside'
              ? // 周辺タイルで確認できた範囲での非該当(隣接自治体の計画だけが写る場合あり)。市の判断とは断定しない。
                '周辺で確認できた区域には含まれていません。市町村が立地適正化計画を定めているか、区域図とあわせて自治体サイトでご確認ください'
              : 'この周辺では立地適正化計画のデータを確認できませんでした（定めていない自治体も多くあります）',
      status: inducement === 'inside' ? 'safe' : 'neutral',
      statusLabel:
        inducement === 'inside'
          ? '区域内'
          : inducement === 'zone_other'
            ? '区域内・要確認'
            : inducement === 'outside'
              ? '該当なし'
              : 'データなし',
    });
  }
  if (stationTrend) {
    rows.push({
      icon: 'train',
      label: '駅の利用者数のうつりかわり',
      detail: `${stationTrend.station ?? '最寄り'}駅${stationTrend.line ? `（${stationTrend.line}）` : ''} ${stationTrend.baseYear}→${stationTrend.latestYear}年度: ${stationTrend.base.toLocaleString('ja-JP')}→${stationTrend.latest.toLocaleString('ja-JP')}人/日`,
      status: 'neutral',
      statusLabel: formatChangePct(stationTrend.changePct),
    });
  }

  return {
    hasAnything:
      population != null || didLayer != null || hasInducementMap || stationTrend != null,
    lead: LEAD,
    verdict,
    facts,
    population: populationBlock,
    shape: { intro: SHAPE_INTRO, maps },
    rows,
    checkItems: [...(prospects?.checkItems ?? []), ...CH5_CHECK_ITEMS],
    emptyText: EMPTY_TEXT,
  };
}

export default function ChapterProspects({ portfolio, planning, site }: Props) {
  const content = useMemo(
    () => buildProspectsContent(portfolio, planning, site),
    [portfolio, planning, site],
  );
  const { verdict, facts, population, shape } = content;

  return (
    <ChapterSection id="prospects">
      <ChapterLead {...content.lead} />

      {!content.hasAnything ? (
        <EmptyState>{content.emptyText}</EmptyState>
      ) : (
        <>
          {/* A. ひとことでいうと（総評カード）。スコアやバッジは出さず、語りと事実チップのみ。 */}
          <div className={styles.verdict}>
            <div className={styles.verdictLabel}>ひとことでいうと</div>
            <h3 className={styles.verdictHeading}>{verdict.heading}</h3>
            <p className={styles.verdictBody}>{verdict.body}</p>
            {facts.length > 0 && (
              <div className={styles.facts}>
                {facts.map((fact, i) => (
                  <span key={i} className={styles.fact}>
                    {fact}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* B. 人のものがたり（人口推移チャート + 高齢化率 + 推計前提の注記） */}
          {population && (
            <div className={styles.block}>
              <h4 className={styles.blockTitle}>人のものがたり</h4>
              <p className={styles.blockNote}>{population.intro}</p>
              <TrendChart
                points={population.chart}
                formatValue={population.formatValue}
                ariaLabel={population.ariaLabel}
              />
              {population.agingText && <p className={styles.aging}>{population.agingText}</p>}
              <p className={styles.note}>{population.note}</p>
            </div>
          )}

          {/* C. まちのかたちのものがたり（メッシュ増減コロプレス + DID + 居住誘導区域） */}
          <div className={styles.block}>
            <h4 className={styles.blockTitle}>まちのかたちのものがたり</h4>
            <p className={styles.blockNote}>{shape.intro}</p>
            {shape.maps.map((spec) => (
              <ChapterMap key={spec.key} spec={spec} />
            ))}
          </div>

          {/* D. 見通しの読みどころ（データがある行だけ表示） */}
          <RowList rows={content.rows} />

          {/* E. 確かめたいこと */}
          <CheckList items={content.checkItems} />
        </>
      )}

      <RawDetails portfolio={portfolio} />
    </ChapterSection>
  );
}
